import type { History, Location } from "history";
import type { NavigateOptions } from "react-router-dom-v5-compat";

/**
 * A consistent way to "enter" edit mode. Call this when you want to programmatically start editing.
 * While this is currently a very simple function, make sure to use this for view/edit
 * mode so that we can easily change the behavior in the future if needed.
 *
 * @param location the current location object
 * @param history the history object
 * @param replace if true, replace the current history entry instead of pushing a new one
 */
export function enterEditMode(location: Location, history: History, replace = false) {
    const historyFn = replace ? history.replace : history.push;

    historyFn({
        ...location,
        state: {
            ...(location.state ?? {}),
            edit: true,
        },
    });
}

/**
 * A consistent way to check if the current location is in edit mode.
 * While this is currently a very simple function, make sure to use this for view/edit
 * mode so that we can easily change the behavior in the future if needed.
 *
 * @param location the current location object
 * @returns true if the current location is in edit mode, false otherwise
 */
export function isInEditMode(location: Location) {
    return location.state?.["edit" as keyof Location["state"]] === true;
}

/**
 * A consistent way to "leave" edit mode. Call this after saving changes.
 * While this is currently a very simple function, make sure to use this for view/edit
 * mode so that we can easily change the behavior in the future if needed.
 *
 * @param history an instance of the history object
 */
export function exitEditMode(history: History) {
    history.goBack();
}

/**
 * A consistent way to set the edit mode for a given path.
 * This is used to create a link that will enter edit mode when clicked.
 *
 * @param newValue Are we entering or exiting edit mode?
 * @param location The current location object
 * @param history The history object
 */
export function setEditMode(newValue: boolean, location: Location, history: History) {
    if (newValue) {
        enterEditMode(location, history);
    } else {
        exitEditMode(history);
    }
}

export interface IEditModeLink {
    /**
     * The pathname to navigate to when the link is clicked.
     */
    pathname: string;
    /**
     * The state to pass to the location when the link is clicked.
     * This should include an `edit` property set to true to indicate that the link is in edit mode.
     */
    state: { edit: boolean };
}

/**
 * Type guard to check if a given path is an edit mode link or a string path.
 * This is useful for ensuring that we are working with the correct type when dealing with links.
 * @param to the path to check
 * @returns true if the path is an edit mode link, false otherwise
 */
export const isIEditModeRoute = (to: string | IEditModeLink): to is IEditModeLink => {
    return typeof to === "object" && "pathname" in to && "state" in to && "edit" in to.state;
};

/**
 * A consistent way to get the edit link props for a given path.
 * This is used to create a link that will enter edit mode when clicked.
 * While this is currently a very simple function, make sure to use this for view/edit
 * mode so that we can easily change the behavior in the future if needed.
 *
 * @param to the path to create the edit link props for
 * @returns the edit link props for the given path
 */
export function getEditModeLink(to: string): IEditModeLink {
    return {
        pathname: to,
        state: { edit: true },
    };
}

/**
 * {@link getEditModeLink} for react-router v6: `navigate(path, getEditModeNavOptions())`.
 *
 * v6 takes the path and the state as two separate arguments, so `getEditModeLink`'s single
 * `{ pathname, state }` object doesn't fit — pass the path first and this second.
 *
 * Like `getEditModeLink`, this navigates to a different route and so drops the current location's
 * state. Use {@link enterEditMode} to edit the page you're already on.
 *
 * @param options other navigate options, e.g. `replace`
 * @returns navigate options that open the destination in edit mode
 */
export function getEditModeNavOptions(options?: Omit<NavigateOptions, "state">): NavigateOptions {
    return {
        ...options,
        state: { edit: true },
    };
}
